
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';


import { SELECT_ANNOTATION, SET_ANNOTATION } from '../../actions';
import randomId from '../../utils/randomId';
import debounce from '../../utils/debounce';
import { isAnnotationIdLocked } from '../../utils/annotationLockUtils';
import { TOOLS_IDS } from '../../utils/constants';
import { isCanvasPanMode } from '../../utils/canvasInteractionMode';
import { useStore } from '../../hooks';
import previewThenCallAnnotationAdding from './previewThenCallAnnotationAdding';
import useDebouncedCallback from '../useDebouncedCallback';

// TODO: Imporve the logic and separate the selected annotation options from handling preview and options before draw.
const useAnnotation = (annotation = {}, enablePreview = true) => {
  const {
    dispatch,
    previewGroup,
    annotations,
    selectionsIds = [],
    config,
    editorPreferences = {},
  } = useStore();
  const allowDrawPreview = enablePreview && !isCanvasPanMode(editorPreferences);
  const annotationDefaults = {
    ...config.annotationsCommon,
    ...config[annotations[selectionsIds[0]]?.name || annotation.name],
  };
  const [tmpAnnotation, setTmpAnnotation] = useState(() => ({
    ...annotationDefaults,
    ...annotation,
    ...annotations[selectionsIds[0]],
  }));
  const tmpAnnotationRef = useRef(tmpAnnotation);
  const annotationBeforeSelection = useRef();
  const annotationsRef = useRef(annotations);
  const canvas = previewGroup?.getStage();

  tmpAnnotationRef.current = tmpAnnotation;
  annotationsRef.current = annotations;

  const saveAnnotation = useCallback((annotationData) => {
    const { fonts, onFontChange, ...savableAnnotationData } = annotationData;
    dispatch({
      type: SET_ANNOTATION,
      payload: savableAnnotationData,
    });
    if (savableAnnotationData.id && annotation.name !== TOOLS_IDS.PEN) {
      debounce(() => {
        dispatch({
          type: SELECT_ANNOTATION,
          payload: {
            annotationId: savableAnnotationData.id,
          },
        });
      }, 30)();
    }
  }, [annotation.name, dispatch]);

  const updateTmpAnnotation = useDebouncedCallback((updatesObjOrFn) => {
    setTmpAnnotation((latest) => ({
      ...latest,
      shouldSave: false,
      neverSave: false,
      ...(typeof updatesObjOrFn === 'function'
        ? updatesObjOrFn(latest)
        : updatesObjOrFn),
    }));
  }, 15);

  const getAnnotationInitialProps = useCallback(
    (currentAnnotation, newAnnotationName) => {
      if (currentAnnotation.name === newAnnotationName) {
        const {
          x,
          y,
          width,
          height,
          radius,
          radiusX,
          radiusY,
          innerRadius,
          outerRadius,
          numPoints,
          sides,
          points,
          image,
          text,
          scaleX,
          scaleY,
          rotation,
          id: _id,
          locked: _locked,
          groupId: _groupId,
          neverSave: _neverSave,
          shouldSave: _shouldSave,
          ...dimensionlessProps
        } = currentAnnotation;

        return {
          ...annotationDefaults,
          ...annotation,
          ...dimensionlessProps,
        };
      }

      return {
        ...annotationDefaults,
        ...annotation,
      };
    },
    [],
  );

  const saveAnnotationNoDebounce = useCallback((newAnnotationData) => {
    if (
      newAnnotationData.id &&
      isAnnotationIdLocked(annotations, newAnnotationData.id)
    ) {
      return;
    }
    setTmpAnnotation((latest) => {
      const isNewAnnotation = !newAnnotationData.id;
      const initialProps = getAnnotationInitialProps(
        latest,
        newAnnotationData.name || annotation.name,
      );
      const next = {
        ...initialProps,
        ...newAnnotationData,
        id:
          newAnnotationData.id ||
          randomId(newAnnotationData.name || latest.name),
        shouldSave: true,
        neverSave: false,
      };

      if (isNewAnnotation) {
        next.locked = false;
        delete next.groupId;
      }

      return next;
    });
  }, [annotation.name, annotations, getAnnotationInitialProps]);

  useEffect(() => {
    const { shouldSave, neverSave, ...savableAnnotation } = tmpAnnotation;
    const selectedAnnotation =
      selectionsIds.length === 1 ? annotations[selectionsIds[0]] : null;

    if (neverSave) {
      return;
    }

    const targetId = shouldSave ? savableAnnotation.id : selectedAnnotation?.id;
    if (!targetId || isAnnotationIdLocked(annotations, targetId)) {
      return;
    }

    const isEditingSelectedAnnotation =
      shouldSave ||
      (selectedAnnotation && savableAnnotation.id === selectedAnnotation.id);

    if (isEditingSelectedAnnotation) {
      saveAnnotation({
        ...savableAnnotation,
        id: targetId,
      });
    }
  }, [annotations, saveAnnotation, selectionsIds, tmpAnnotation]);

  useLayoutEffect(() => {
    if (selectionsIds.length === 1) {
      annotationBeforeSelection.current = tmpAnnotationRef.current;
      setTmpAnnotation({ ...annotations[selectionsIds[0]], neverSave: true });
    } else if (annotationBeforeSelection.current) {
      setTmpAnnotation({
        ...annotationBeforeSelection.current,
        neverSave: true,
      });
      annotationBeforeSelection.current = null;
    }
  }, [selectionsIds, annotations]);

  useEffect(() => {
    let stopAnnotationEventsListening = null;

    if (canvas && allowDrawPreview) {
      const annotationInitialProps = getAnnotationInitialProps(
        tmpAnnotation,
        annotation.name,
      );

      stopAnnotationEventsListening = previewThenCallAnnotationAdding(
        canvas,
        { ...annotationInitialProps, name: annotation.name },
        previewGroup,
        saveAnnotationNoDebounce,
        annotations,
        annotationsRef,
      );
    }

    return () => {
      if (stopAnnotationEventsListening) {
        stopAnnotationEventsListening();
      }
    };
  }, [allowDrawPreview, annotation, annotations, canvas, previewGroup, saveAnnotationNoDebounce, tmpAnnotation]);

  return useMemo(
    () => [tmpAnnotation, updateTmpAnnotation, saveAnnotationNoDebounce],
    [tmpAnnotation, updateTmpAnnotation, saveAnnotationNoDebounce],
  );
};

export default useAnnotation;
